import * as React from 'preact/compat'
import { type GameInfo, types } from '../SeriesEditor/types'
import styles from '../../../css/game-series-scheme.pcss.json'
import { SeriesSchemeState } from './state'
import cn from 'classnames'
import { gameDateToInt, wait } from '../../common/helpers'
import SVG from '../SVG'
import ToLibraryButton from './ToLibraryButton'
import buttonsCss from '../../../css/buttons.pcss.json'
import type { TargetedEvent } from 'preact/compat'

interface PropTypes {
  game: GameInfo,
}

const GameRenderer = ({ game }: PropTypes) => {
  const context = React.useContext(SeriesSchemeState)
  const container = React.useRef<HTMLDivElement>()

  React.useEffect(() => {
    context.registerGameElement(game.id, container.current)
  }, [game.id, container.current])

  const outsideClick = React.useCallback((e: MouseEvent) => {
    const panzoom = document.querySelector<HTMLElement & {
      dragging: boolean
    }>('.react-transform-wrapper')
    if (panzoom.dragging
      || e.target === container.current
      || (e.target as HTMLElement).closest(`.${styles['game']}`) === container.current) {
      return
    }
    context.clearHighlight()
    panzoom.removeEventListener('mouseup', outsideClick)
  }, [])

  const registerOutsideClick = React.useCallback(() => {
    const panzoom = document.querySelector<HTMLElement>('.react-transform-wrapper')
    panzoom.removeEventListener('mouseup', outsideClick)
    panzoom.addEventListener('mouseup', outsideClick)
  }, [outsideClick])

  const highlightGame = React.useCallback(() => {
    const container = document.querySelector<HTMLElement & {
      dragging: boolean
    }>('.react-transform-wrapper')
    if (container.dragging) {
      return
    }
    context.highlightGame(game.id)
    wait(300).then(() => {
      registerOutsideClick()
    })
  }, [game.id, registerOutsideClick])

  const showInfoPanel = React.useCallback(() => {
    context.showInfoPanel(game.id)
  }, [game.id])

  const dateParts = game.release_date.split('-')
  let year = dateParts[0]
  if (year === '0000') {
    year = '????'
  }
  if (year === '0001') {
    year = '----'
  }

  const highlightInfo = context.relatedGamesHighlights.value.find(info => info.id === game.id)
  const highlightedGame = context.highlightedGame.value

  const removeGame = React.useCallback((e: TargetedEvent) => {
    e.preventDefault()
    e.stopPropagation()
    context.removeGame(game.id)
  }, [game.id])

  const showRelationModal = React.useCallback((e: TargetedEvent) => {
    e.preventDefault()
    e.stopPropagation()
    context.showRelationsModal(game.id)
  }, [game.id])

  return <div className={styles['game-container']}>
    <div className={cn({
      [styles['game']]: true,
      [styles['game--highlighted']]: highlightedGame === game.id,
      [styles['game--connected']]: !!highlightInfo,
      [styles['game--disabled']]: highlightedGame && highlightedGame !== game.id && !highlightInfo
    })} id={`node_${game.id}`} ref={container} onClick={highlightGame}>
      <div className={styles['game__top']}>
        {highlightInfo && <span class={styles['game__relation-info']}>{highlightInfo.text}</span>}
        {!highlightInfo && <span>{types[game.type]}</span>}
        <span>{year}</span>
      </div>
      <div className={styles['game__image__container']}>
        <img className={styles['game__image']} alt='' src={game.poster} draggable={false} width={224} height={224} />
        {context.editable.value && <div className={styles['game__editor-buttons']}>
            <button className={cn(buttonsCss['button--icon'], buttonsCss['on-image'])} onClick={removeGame} style='--button-icon-size: 32px; --button-padding: 0; --button-width: 32px; height: 32px;'>
                <SVG i='sg-new/cross' adaptive/>
            </button>
            <button type='button' className={cn(buttonsCss['button--icon'], buttonsCss['on-image'])} style='--button-icon-size: 24px; --button-padding: 4px; --button-width: 32px; height: 32px;' onClick={showRelationModal}>
                <SVG i='sg-new/edit' adaptive/>
            </button>
        </div>}
        {!context.editable.value && <>
          <ToLibraryButton status={game.status} id={game.id} />
          {context.isMobile.value && <button className={styles['game__info-button']} onClick={showInfoPanel}>
              <SVG i='sg-new/info' adaptive />
          </button>}
        </>}
      </div>
      <span className={styles['game__title']}>{game.title}</span>
    </div>
    {game.children && game.children.length > 0 && <div className={styles['cell']}>
      {game.children
        .sort((game1, game2) => gameDateToInt(game1.release_date) - gameDateToInt(game2.release_date))
        .map(childGame => <GameRenderer game={childGame} />)}
    </div>}
  </div>
}

export default GameRenderer
